/**
 * messageTemplatesApi — thin wrapper over the shared backend catalog at
 * /api/message-templates. Used by the admin management page today; will
 * be consumed by BulkWhatsappModal and LeadTemplates in a follow-up PR
 * once the backend endpoints are live and the frontend fallback drops.
 *
 * Every helper takes a plain object and returns the parsed response body.
 * Errors are re-thrown as-is so callers can render `formatError(e)` with
 * the existing pattern.
 */
import { api } from "@/lib/api";

const BASE = "/message-templates";

export async function listMessageTemplates({ channel, stage, includeArchived = false } = {}) {
  const params = {};
  if (channel) params.channel = channel;
  if (stage) params.stage = stage;
  if (includeArchived) params.include_archived = true;
  const { data } = await api.get(BASE, { params });
  return Array.isArray(data?.templates) ? data.templates : [];
}

export async function createMessageTemplate(payload) {
  const { data } = await api.post(BASE, payload);
  return data;
}

export async function updateMessageTemplate(templateId, patch) {
  const { data } = await api.patch(`${BASE}/${templateId}`, patch);
  return data;
}

export async function archiveMessageTemplate(templateId) {
  const { data } = await api.post(`${BASE}/${templateId}/archive`, {});
  return data;
}

export async function unarchiveMessageTemplate(templateId) {
  const { data } = await api.post(`${BASE}/${templateId}/unarchive`, {});
  return data;
}

export async function setDefaultMessageTemplate(templateId) {
  const { data } = await api.post(`${BASE}/${templateId}/set-default`, {});
  return data;
}

export async function resetBuiltinMessageTemplate(templateId) {
  const { data } = await api.post(`${BASE}/${templateId}/reset-builtin`, {});
  return data;
}

export async function seedMessageTemplates() {
  const { data } = await api.post(`${BASE}/seed`, {});
  return data;
}

// ---------------------------------------------------------------------------
// Stage-aware selection helpers — shared by BulkWhatsappModal and
// LeadTemplates so both composers agree on "which template is recommended"
// without duplicating the ranking logic.
// ---------------------------------------------------------------------------

function activeOnly(templates) {
  return (templates || []).filter((t) => t && t.is_active);
}

/**
 * Recommended template for a given stage. Preference chain:
 *   1) Active default for the exact stage.
 *   2) Any active template of that stage (sorted by name for stability).
 *   3) Active global (stage=null) template — default first, then by name.
 *   4) First active template overall.
 * Returns null when nothing usable exists.
 */
export function pickDefaultTemplateForStage(templates, stage) {
  const active = activeOnly(templates);
  if (active.length === 0) return null;
  if (stage) {
    const stageDefault = active.find((t) => t.stage === stage && t.is_default_for_stage);
    if (stageDefault) return stageDefault;
    const stageMatches = active
      .filter((t) => t.stage === stage)
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    if (stageMatches.length > 0) return stageMatches[0];
  }
  const globals = active
    .filter((t) => t.stage == null)
    .sort((a, b) => {
      if (a.is_default_for_stage !== b.is_default_for_stage) return a.is_default_for_stage ? -1 : 1;
      return (a.name || "").localeCompare(b.name || "");
    });
  if (globals.length > 0) return globals[0];
  return active[0];
}

/**
 * Selector-ordered list for a given stage. Ranking:
 *   1) Active default for the exact stage.
 *   2) Other active templates of that stage (by name).
 *   3) Active globals (stage=null) — default first, then by name.
 *   4) The rest of active templates (by stage index, then by name).
 * Archived rows are dropped so the dropdown never surfaces them.
 */
export function orderTemplatesForStage(templates, stage) {
  const active = activeOnly(templates);
  if (active.length === 0) return [];
  const byName = (a, b) => (a.name || "").localeCompare(b.name || "");
  const stageDefault = stage ? active.find((t) => t.stage === stage && t.is_default_for_stage) : null;
  const stageOthers = stage
    ? active.filter((t) => t.stage === stage && !t.is_default_for_stage).sort(byName)
    : [];
  const globals = active
    .filter((t) => t.stage == null)
    .sort((a, b) => {
      if (a.is_default_for_stage !== b.is_default_for_stage) return a.is_default_for_stage ? -1 : 1;
      return byName(a, b);
    });
  const usedIds = new Set([
    ...(stageDefault ? [stageDefault.template_id] : []),
    ...stageOthers.map((t) => t.template_id),
    ...globals.map((t) => t.template_id),
  ]);
  const rest = active
    .filter((t) => !usedIds.has(t.template_id))
    .sort((a, b) => {
      const sa = a.stage || ""; const sb = b.stage || "";
      if (sa !== sb) return sa.localeCompare(sb);
      return byName(a, b);
    });
  return [
    ...(stageDefault ? [stageDefault] : []),
    ...stageOthers,
    ...globals,
    ...rest,
  ];
}
